import { ReservationTourist, StatusForAgentEnum } from '@appTypes/api.types';
import { ApiErrorResponse } from '@core/interceptors/api.interceptor';
import {
  RES_TOURIST_DOCUMENT_UPLOAD_ALLOWED_STATUS_MESSAGE,
  RES_TOURIST_DOCUMENT_UPLOAD_NOT_ALLOWED_ERROR_CODE,
  RESERVATION_STATUS_FILTERS
} from './reservations.const';

/**
 * Grey of a status the filter list does not know, and of an unknown Paid Status (R-WRL17 / R-WRL17a): the theme's grey-400
 * (`--color-kz-text-tertiary`). It was `text-text-gray-400`, a class no CSS rule defines, so those statuses read in the body colour (website-web#89).
 */
export const RESERVATION_STATUS_GREY_CLASS = 'text-kz-text-tertiary';

/** How a reservation status is shown to the agent: its label (a translation key) and colour class. */
export interface ReservationStatusDetails {
  label: string;
  styleClass: string;
}

/**
 * The label and colour a reservation status is shown with — `Canceled` reads "Rejected", and so on.
 * A status the filter list does not know (`Pending`, `Draft`, …) is shown as is, in grey.
 */
export const resolveReservationStatusDetails = (statusForAgent?: StatusForAgentEnum): ReservationStatusDetails => {
  const status = RESERVATION_STATUS_FILTERS.find((s) => s.value === statusForAgent);
  const fallbackLabel = statusForAgent ?? 'Unknown';
  return status
    ? { label: status.label ?? fallbackLabel, styleClass: status.styleClass ?? '' }
    : { label: fallbackLabel, styleClass: RESERVATION_STATUS_GREY_CLASS };
};

/**
 * Text of the API's refusal to upload a tourist document, or `null` when the failure is not that
 * refusal and the generic error dialog applies (KM-5604).
 *
 * The API re-checks the reservation status in NugiosCore on every upload and, once the reservation
 * has left `New`, answers `UNABLE_UPLOAD_DOCUMENT_DUE_RES_STATUS` with a `userErrorDescription` —
 * that text is what the popup shows inline. A refusal carrying only its code (no text, or the code
 * echoed back in place of one) falls back to the agreed message.
 */
export const resolveTouristDocumentUploadRefusalMessage = (error: ApiErrorResponse): string | null => {
  const { errorCode, displayErrorMessage } = error;
  if (errorCode !== RES_TOURIST_DOCUMENT_UPLOAD_NOT_ALLOWED_ERROR_CODE) return null;
  return displayErrorMessage && displayErrorMessage !== errorCode
    ? displayErrorMessage
    : RES_TOURIST_DOCUMENT_UPLOAD_ALLOWED_STATUS_MESSAGE;
};

/** The tourist's "First Last", skipping whichever part the API left empty. */
export const reservationTouristFullName = (tourist: ReservationTourist): string =>
  [tourist.firstName, tourist.lastName].filter(Boolean).join(' ');
